import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Textarea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useRepository,
  SignInPrompt,
  useAuth
} from "@city-sage/aurum-avis-labs-components";
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Camera, Edit2, Calendar, Heart, Plus, ArrowLeft, MessageSquare, User, Coins, CreditCard, Info } from 'lucide-react';
import { QueryKeys } from '../../lib/QueryKeys';
import { PostCard } from '../actions/PostCard';
import { Link } from '@tanstack/react-router';
import { ShareButton } from '../actions/ShareButton';
import { toast } from 'sonner';
import { useFollowStats } from '../../lib/useFollow';
import CompactHeader from '../CompactHeader';
import { uploadFileCompressed } from '../../lib/imageUtils';
import { cn } from '@city-sage/aurum-avis-labs-components';
import { useFormattedDate } from '../../lib/dateUtils';

// Import types from the generated client
import { CreditPackage } from '@city-sage/core-client';

// Stats Component
interface Stats {
  posts: number;
  reactions: number;
  followers: number;
  following: number;
}

interface MobileProfileStatsProps {
  stats: Stats;
}

function MobileProfileStats({ stats }: MobileProfileStatsProps) {
  const { t } = useTranslation();
  
  const statItems = [
    { label: t('pages.profile.stats.posts'), value: stats.posts },
    { label: t('pages.profile.stats.reactions'), value: stats.reactions },
    { label: t('pages.profile.stats.followers'), value: stats.followers },
    { label: t('pages.profile.stats.following'), value: stats.following },
  ];

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  return (
    <div className="bg-white mx-4 -mt-6 relative z-10 rounded-2xl shadow-sm">
      <div className="grid grid-cols-4 divide-x divide-gray-100">
        {statItems.map((item, index) => (
          <button
            key={item.label}
            className="p-4 text-center hover:bg-gray-50 transition-colors first:rounded-l-2xl last:rounded-r-2xl"
          >
            <div className="text-xl font-bold text-gray-900">{formatNumber(item.value)}</div>
            <div className="text-xs text-gray-600 font-medium">{item.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Credits Purchase Modal Component
interface CreditsPurchaseModalProps {
  account: any;
}

function CreditsPurchaseModal({ account }: CreditsPurchaseModalProps) {
  const { t } = useTranslation();
  const { api } = useRepository();
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch credit packages from the API
  const { data: packages, isLoading: isLoadingPackages } = useQuery({
    queryKey: ['credit-packages', account?.id],
    queryFn: () => api.getCreditPackages({ accountId: account?.id! }),
    enabled: !!account?.id && isOpen
  });

  const { mutate: purchasePackage, isPending } = useMutation({
    mutationFn: (pkg: CreditPackage) => api.createOrder({
      accountId: account?.id!,
      createOrderRequest: {
        packageId: pkg.id,
      },
    }),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.account.details() });
      window.location.href = res.stripeCheckoutUrl;
    },
    onError: () => {
      toast.error('Failed to create order. Please try again.');
    }
  });

  const handlePurchase = () => {
    if (selectedPackage) {
      purchasePackage(selectedPackage);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          Get Credits
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Purchase Credits
          </DialogTitle>
          <DialogDescription>
            Select a credit package to continue using AI features
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {isLoadingPackages ? (
            <div className="grid grid-cols-1 gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="animate-pulse border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="h-4 bg-gray-300 rounded w-20 mb-2"></div>
                      <div className="h-3 bg-gray-300 rounded w-16"></div>
                    </div>
                    <div className="text-right">
                      <div className="h-6 bg-gray-300 rounded w-12 mb-1"></div>
                      <div className="w-4 h-4 bg-gray-300 rounded-full ml-auto"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {packages?.map((pkg) => (
                <div 
                  key={pkg.id} 
                  className={cn(
                    "border rounded-lg p-4 relative cursor-pointer transition-all",
                    pkg.popular && "border-blue-500",
                    selectedPackage?.id === pkg.id ? "ring-2 ring-blue-500 bg-blue-50" : "hover:border-blue-300"
                  )}
                  onClick={() => setSelectedPackage(pkg)}
                >
                  {pkg.popular && (
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                      Most Popular
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{pkg.name}</h4>
                      <div className="text-sm text-gray-600 mt-1">
                        {pkg.credits} credits
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold">${pkg.price}</div>
                      <div className={cn(
                        "w-4 h-4 rounded-full border-2 ml-auto mt-1",
                        selectedPackage?.id === pkg.id ? "bg-blue-500 border-blue-500" : "border-gray-300"
                      )}/>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <Button 
            className="w-full"
            onClick={handlePurchase}
            disabled={isPending || !selectedPackage || isLoadingPackages}
            size="lg"
          >
            {isPending ? "Processing..." : isLoadingPackages ? "Loading packages..." : selectedPackage 
              ? `Purchase ${selectedPackage.credits} Credits for $${selectedPackage.price}` 
              : "Select a package to purchase"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Credits Section Component
interface CreditsSectionProps {
  account: any;
}

function CreditsSection({ account }: CreditsSectionProps) {
  const { t } = useTranslation();
  const credits = account?.credits ?? 0;

  return (
    <div className="bg-white mx-4 my-6 rounded-2xl shadow-sm border border-gray-200">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
              <Coins className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Credits</h2>
              <p className="text-sm text-gray-600">AI-powered features</p>
            </div>
          </div>
        </div>

        {/* Credit Display */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-1">{credits}</div>
              <p className="text-sm text-gray-600">
                {credits === 1 ? 'Credit available' : 'Credits available'}
              </p>
            </div>
            <CreditsPurchaseModal account={account} />
          </div>
        </div>

        {/* Info */}
        <div className="flex items-start gap-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="mt-0.5 cursor-help">
                  <Info className="h-4 w-4 text-blue-500" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm">1 credit = 1 stop creation or AI interaction</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="flex-1">
            <p className="text-xs text-gray-600 leading-relaxed">
              Use credits for AI chat interactions and creating itinerary stops
            </p>
            <p className="text-xs text-blue-600 font-medium mt-1">
              1 stop creation = 1 credit
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Form types
interface AboutMeFormData {
  aboutMe: string;
}

export default function ProfilePage() {
  const { t } = useTranslation();
  const { formatDateShort } = useFormattedDate();
  const { userEmail } = useAuth();
  const isAuthenticated = useIsAuthenticated();


  const [isEditingAbout, setIsEditingAbout] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Form setup
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting }
  } = useForm<AboutMeFormData>({
    defaultValues: {
      aboutMe: ''
    }
  });

  const watchedAboutMe = watch('aboutMe');

  const { api } = useRepository();

  const { data: account, isLoading: isLoadingAccount } = useQuery({
    queryKey: ['account'],
    queryFn: () => api.getMyAccount(),
    enabled: !!userEmail
  });

  const queryClient = useQueryClient();
  
  // Use follow stats hook for current user
  const { followerCount, followingCount } = useFollowStats({ 
    userId: account?.id || '' 
  });
  
  // Fetch user's posts with infinite scroll
  const {
    data: postsData,
    isLoading: isLoadingPosts,
    fetchNextPage: fetchNextPosts,
    hasNextPage: hasNextPosts,
    isFetchingNextPage: isFetchingNextPosts
  } = useInfiniteQuery({
    queryKey: QueryKeys.post.all({ filter: JSON.stringify({ accountId: account?.id }) }),
    queryFn: async ({ pageParam = 1 }) => {
      return api.findAllPost({ 
        filter: JSON.stringify({ accountId: account?.id }),
        page: pageParam,
        limit: 10,
        sort: JSON.stringify(['createdAt', "DESC"])
      });
    },
    enabled: !!account?.id,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !Array.isArray(lastPage) || lastPage.length < 10) return undefined;
      return (allPages?.length ?? 0) + 1;
    },
    initialPageParam: 1,
  });

  // Fetch user's reactions with infinite scroll
  const {
    data: reactionsData,
    isLoading: isLoadingReactions,
    fetchNextPage: fetchNextReactions,
    hasNextPage: hasNextReactions,
    isFetchingNextPage: isFetchingNextReactions
  } = useInfiniteQuery({
    queryKey: ['reactions', account?.id],
    queryFn: async ({ pageParam = 1 }) => {
      return api.findAllReaction({ 
        filter: JSON.stringify({ accountId: account?.id }),
        page: pageParam,
        limit: 10,
        sort: JSON.stringify(['createdAt', "DESC"])
      });
    },
    enabled: !!account?.id,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !Array.isArray(lastPage) || lastPage.length < 10) return undefined;
      return (allPages?.length ?? 0) + 1;
    },
    initialPageParam: 1,
  });

  // Flatten infinite query results
  const posts = postsData?.pages?.flat() || [];
  const reactions = reactionsData?.pages?.flat() || [];

  const { mutate: updateAboutMe } = useMutation({
    mutationFn: (data: AboutMeFormData) => 
      api.updateAccount({ id: account?.id!, updateAccountDto: { aboutMe: data.aboutMe } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] });
      setIsEditingAbout(false);
      toast.success(t('pages.profile.about.saved'));  
    },
    onError: () => {
      toast.error('Failed to update about me. Please try again.');
    }
  });

  const { mutate: uploadAvatar, isPending: isUploadingAvatar } = useMutation({
    mutationFn: async (file: File) => {
      // First upload the file with compression
      const uploadResponse = await uploadFileCompressed(file, api, {
        maxWidth: 512,
        maxHeight: 512,
        quality: 0.8
      });
      
      // Then update the account with the new avatar URL
      await api.updateAccount({ 
        id: account?.id!, 
        updateAccountDto: { avatarUrl: uploadResponse.url } 
      });
      
      return uploadResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] });
      setAvatarPreview(null); // Clear preview since we now have the real image
      toast.success(t('pages.profile.avatar.saved'));
    },
    onError: () => {
      toast.error('Failed to update profile picture. Please try again.');
    }
  });

  // Initialize form when account data loads
  useEffect(() => {
    if (account?.aboutMe) {
      reset({ aboutMe: account.aboutMe });
    }
  }, [account?.aboutMe, reset]);

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      // Upload the file
      uploadAvatar(file);
    }
  };

  const onSubmitAboutMe = (data: AboutMeFormData) => {
    updateAboutMe(data);
  };

  const handleCancelAbout = () => {
    reset({ aboutMe: account?.aboutMe || '' });
    setIsEditingAbout(false);
  };

  // Calculate social stats
  const stats = {
    posts: posts?.length || 0,
    reactions: reactions?.length || 0,
    followers: followerCount,
    following: followingCount,
  };


  // Show sign-in prompt if user is not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-dvh bg-gray-50">
        <CompactHeader 
          icon={User} 
          title={t('pages.profile.header.title')} 
          subtitle={t('pages.profile.header.subtitle')} 
        />
        <div className="container mx-auto px-4 py-6">
          <SignInPrompt
            title={t('pages.profile.signInRequired.title', 'Sign in to view your profile')}
            description={t('pages.profile.signInRequired.description', 'Create an account or sign in to manage your profile, view your posts, and track your travel memories.')}
            icon={<User className="h-8 w-8 text-blue-500" />}
          />
        </div>
      </div>
    )
  }

  if (isLoadingAccount) {
    return (
      <div className="min-h-dvh bg-gray-50">
        <div className="animate-pulse">
          <div className="h-14 bg-white border-b border-gray-100"></div>
          <div className="bg-backgroundpx-4 py-8">
            <div className="text-center space-y-4">
              <div className="w-24 h-24 bg-white/30 rounded-full mx-auto"></div>
              <div className="space-y-2">
                <div className="h-6 bg-white/30 rounded w-48 mx-auto"></div>
                <div className="h-4 bg-white/30 rounded w-32 mx-auto"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
          <div className="min-h-dvh bg-gray-50 pb-20">

      <CompactHeader 
        icon={User} 
        title={t('pages.profile.header.title')} 
        subtitle={t('pages.profile.header.subtitle')} 
      />

      {/* Profile Header */}
      <div  className="px-4 py-8 mb-4">
        <div className="text-center space-y-4">
          {/* Avatar */}
          <div className="relative inline-block">
            <div className="relative h-24 w-24 mx-auto overflow-hidden rounded-full border-4 border-white/30 shadow-lg">
              <Avatar className="w-full h-full">
                <AvatarImage src={avatarPreview || account?.avatarUrl} className="object-cover" />
                <AvatarFallback className="bg-white/20">
                <Camera className="h-6 w-6" />
                </AvatarFallback>
              </Avatar>
              <div className={`absolute inset-0 ${isUploadingAvatar ? 'bg-black/60' : 'bg-black/40'} ${isUploadingAvatar ? 'opacity-100' : 'opacity-0 hover:opacity-100'} transition-opacity flex items-center justify-center cursor-pointer`}>
                {isUploadingAvatar ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs text-white">Uploading...</span>
                  </div>
                ) : (
                  <Camera className="h-6 w-6 text-white" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  disabled={isUploadingAvatar}
                  className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">@{account?.userName}</h1>
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">Joined {account?.createdAt ? formatDateShort(account.createdAt) : ''}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <MobileProfileStats stats={stats} />

      {/* Credits Section */}
      <CreditsSection account={account} />

      {/* About Me Section */}
      <div className="bg-white mx-4 my-12 rounded-2xl shadow-sm">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.profile.about.title')}</h2>
            {!isEditingAbout && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsEditingAbout(true)}
                className="h-8 w-8 text-gray-500"
              >
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          {isEditingAbout ? (
            <form onSubmit={handleSubmit(onSubmitAboutMe)} className="space-y-3">
              <Textarea
                {...register('aboutMe')}
                placeholder={t('pages.profile.fields.aboutMePlaceholder')}
                className="min-h-[100px] resize-none border-gray-200"
                rows={4}
              />
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" size="sm" onClick={handleCancelAbout}>
                  {t('common.actions.cancel')}
                </Button>
                <Button 
                  type="submit" 
                  size="sm" 
                  disabled={isSubmitting || !isDirty}
                >
                  {isSubmitting ? 'Saving...' : t('common.actions.save')}
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-gray-600 leading-relaxed">
              {watchedAboutMe || account?.aboutMe || t('pages.profile.about.emptyBio')}
            </p>
          )}
        </div>
      </div>

      {/* Content Tabs */}
      <div className="px-4 mt-6">
        <Tabs defaultValue="my-posts" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="my-posts" className="text-xs">{t('pages.profile.tabs.myPosts')}</TabsTrigger>
            <TabsTrigger value="my-reactions" className="text-xs">{t('pages.profile.tabs.myReactions')}</TabsTrigger>
          </TabsList>

          {/* My Posts Tab */}
          <TabsContent value="my-posts" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">{t('pages.profile.posts.title')}</h2>
              </div>
            </div>

            {isLoadingPosts ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="animate-pulse bg-white rounded-2xl shadow-sm p-4">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                        <div className="h-32 bg-gray-300 rounded-xl mt-2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : posts && posts.length > 0 ? (
              <>
                <div className="space-y-4">
                  {posts.map((post) => (
                    <div key={post.id} className="bg-white rounded-2xl shadow-sm">
                      <PostCard
                        post={post}
                        onComment={() => {}}
                      />
                    </div>
                  ))}
                </div>

                {/* Load More Button for Posts */}
                {hasNextPosts && (
                  <div className="flex justify-center pt-6">
                    <Button
                      variant="outline"
                      onClick={() => fetchNextPosts()}
                      disabled={isFetchingNextPosts}
                      className="gap-2"
                    >
                      {isFetchingNextPosts ? 'Loading...' : 'Load More'}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-100 mx-auto flex items-center justify-center mb-6">
                  <MessageSquare className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages.profile.posts.empty.title')}</h3>
                <p className="text-gray-600 mb-6 text-sm">{t('pages.profile.posts.empty.description')}</p>
                <Link to="/">
                  <Button variant="outline" className="gap-2">
                    <Plus className="h-4 w-4" />
                    {t('pages.profile.posts.empty.action')}
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>

          {/* My Reactions Tab */}
          <TabsContent value="my-reactions" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-red-500 flex items-center justify-center">
                  <Heart className="h-4 w-4 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">{t('pages.profile.reactions.title')}</h2>
              </div>
            </div>

            {isLoadingReactions ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="animate-pulse bg-white rounded-2xl shadow-sm p-4">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : reactions && reactions.length > 0 ? (
              <>
                <div className="space-y-4">
                  {reactions.map((reaction) => (
                    <div key={reaction.id} className="bg-white rounded-2xl shadow-sm p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                          <Heart className="w-5 h-5 text-red-500" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">
                            {t('pages.profile.reactions.item', { 
                              type: reaction.reactionType,
                              date: formatDateShort(reaction.createdAt)
                            })}
                          </p>
                          {reaction.reactableType && (
                            <p className="text-xs text-gray-500 mt-1">
                              {t('pages.profile.reactions.target', { target: reaction.reactableType })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Load More Button for Reactions */}
                {hasNextReactions && (
                  <div className="flex justify-center pt-6">
                    <Button
                      variant="outline"
                      onClick={() => fetchNextReactions()}
                      disabled={isFetchingNextReactions}
                      className="gap-2"
                    >
                      {isFetchingNextReactions ? 'Loading...' : 'Load More'}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 mx-auto flex items-center justify-center mb-6">
                  <Heart className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages.profile.reactions.empty.title')}</h3>
                <p className="text-gray-600 mb-6 text-sm">{t('pages.profile.reactions.empty.description')}</p>
                <Link to="/">
                  <Button variant="outline" className="gap-2">
                    <Heart className="h-4 w-4" />
                    {t('pages.profile.reactions.empty.action')}
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
